/* eslint-disable string-to-lingui/missing-lingui-transformation */
import styled from 'styled-components'

import {Box, Inline, Text, tokens, VisuallyHidden} from '@pleo-io/telescope'
import {CheckCircleFilled, ChevronRight} from '@pleo-io/telescope-icons'

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

type ProgressIndicatorProps = {
    steps: {
        label: string
        isCurrentStep?: boolean
        isCompleted: boolean
    }[]
    headingLevel: HeadingLevel
}

export const ProgressIndicator = ({steps, headingLevel}: ProgressIndicatorProps) => {
    return (
        <StepList aria-label={`Progress indicator`}>
            {steps.map(({label, isCurrentStep, isCompleted}, index) => {
                return (
                    <Step
                        key={label}
                        aria-current={isCurrentStep ? 'step' : undefined}
                        $isCurrentStep={isCurrentStep}
                        $isCompleted={isCompleted}
                    >
                        <IndicatorContainer>
                            {!isCompleted && <StepIndicator>{index + 1}</StepIndicator>}
                            {isCompleted && (
                                <CheckCircleFilled
                                    data-testid="completed-checkmark"
                                    size={24}
                                    color={tokens.colorBorderPositive}
                                />
                            )}
                        </IndicatorContainer>

                        <StepLabel $headingLevel={headingLevel}>
                            {isCompleted && <VisuallyHidden>Completed step</VisuallyHidden>}
                            {label}
                        </StepLabel>
                        {index < steps.length - 1 && (
                            <ChevronRight data-testid="chevron-right-separator" size={16} />
                        )}
                    </Step>
                )
            })}
        </StepList>
    )
}

const StepList = styled(Inline).attrs({
    as: 'ol',
    space: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
})`
    box-sizing: border-box;
    list-style: none;

    * {
        box-sizing: border-box;
    }
`

const IndicatorContainer = styled(Box).attrs({mr: 8, 'aria-hidden': true})`
    display: flex;
`

const StepIndicator = styled(Text).attrs({variant: 'small-subtle'})``

const StepLabel = styled(Text).attrs<{$headingLevel: HeadingLevel}>(({$headingLevel}) => ({
    as: $headingLevel,
}))``

const Step = styled(Inline).attrs({as: 'li'})<{$isCurrentStep?: boolean; $isCompleted: boolean}>`
    align-items: center;
    color: ${({$isCurrentStep, $isCompleted}) =>
        $isCurrentStep || $isCompleted
            ? tokens.colorContentInteractive
            : tokens.colorContentInteractiveQuiet};

    ${StepIndicator} {
        border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderStaticLoud};
        border-radius: ${tokens.arc99999};
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        color: inherit;

        ${({$isCurrentStep}) =>
            $isCurrentStep && `border-color: ${tokens.colorBorderInteractiveSelected};`};
    }

    ${StepLabel} {
        white-space: nowrap;
        padding-bottom: ${tokens.spacing8};
        padding-top: ${tokens.spacing8};
        font-weight: ${({$isCurrentStep}) =>
            $isCurrentStep ? tokens.fontWeightSemibold : tokens.fontWeightRegular};
        border-bottom: ${({$isCurrentStep}) =>
            $isCurrentStep ? `1px solid ${tokens.colorBorderInteractiveSelected}` : 'none'};

        &:not(:last-child) {
            margin-right: ${tokens.spacing8};
        }
    }
`
